import type { AuthSessionDto, AuthUserDto, Permission, Role } from '@talentbabu/types';
import { create } from 'zustand';

type AuthStatus = 'loading' | 'authenticated' | 'anonymous';

interface AuthState {
  status: AuthStatus;
  /** Kept in memory only — never persisted to localStorage. The refresh token lives in an httpOnly cookie. */
  accessToken: string | null;
  user: AuthUserDto | null;
  setSession: (session: AuthSessionDto) => void;
  setUser: (user: AuthUserDto) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'loading',
  accessToken: null,
  user: null,
  setSession: (session) => set({ status: 'authenticated', accessToken: session.accessToken, user: session.user }),
  setUser: (user) => set({ user }),
  clear: () => set({ status: 'anonymous', accessToken: null, user: null }),
}));

export function useCurrentUser(): AuthUserDto | null {
  return useAuthStore((s) => s.user);
}

/** UI-only permission check. The API enforces every permission independently. */
export function userCan(user: AuthUserDto | null, permission: Permission): boolean {
  return !!user && (user.role === 'SUPER_ADMIN' || user.permissions.includes(permission));
}

export function homePathFor(role: Role): string {
  switch (role) {
    case 'CANDIDATE':
      return '/candidate/dashboard';
    case 'EMPLOYER':
    case 'HR_MANAGER':
      return '/employer/dashboard';
    case 'RECRUITER':
      return '/recruiter/dashboard';
    default:
      return '/admin/dashboard';
  }
}
